<script setup lang="ts">
// v3.18.2 BT 互联状态 — tracker 注册表 / 做种列表 / DHT, 3s 轮询。
// v3.18.4 节点一览 — 集群全部节点 + IP 内置于 tracker 状态。
// v3.18.5 打磨: 文案接 i18n; 127.0.0.1 自通告行标注为"本机做种引擎"
// 而非陌生 peer; 清理死代码 (infoHashes/activeSeeds)。
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useQuery } from '@tanstack/vue-query'
import { getBTStatus, type BTNodeInfo } from '@/api/cluster'
import FlagBadge from '@/components/tasks/FlagBadge.vue'
import { formatBytes } from '@/composables/useFormat'

const { t } = useI18n()

const q = useQuery({
  queryKey: ['bt-status'],
  queryFn: () => getBTStatus(),
  refetchInterval: 3000,
  // v3.19.28: 同 ClusterTab — 双面板使用时后台标签页也保持轮询。
  refetchIntervalInBackground: true,
})

const bt = computed(() => q.data.value)

// 连接的对端: 环回地址 = 本机做种引擎的自宣 (自指 tracker 兜底),
// 不当陌生 peer 展示 — 归并成一行"本机"。
const allPeers = computed(() => {
  if (!bt.value?.tracker) return []
  const seen = new Set<string>()
  const out: Array<{ ip: string; port: number; seeder: boolean; loopback: boolean }> = []
  let loopbackSeeder = false
  for (const ih of bt.value.tracker) {
    for (const p of ih.peers) {
      const key = `${p.ip}:${p.port}`
      if (seen.has(key)) continue
      seen.add(key)
      if (p.ip === '127.0.0.1' || p.ip === '::1') {
        loopbackSeeder = loopbackSeeder || p.seeder
        continue
      }
      out.push({ ip: p.ip, port: p.port, seeder: p.seeder, loopback: false })
    }
  }
  if (loopbackSeeder) {
    out.push({ ip: '127.0.0.1', port: 0, seeder: true, loopback: true })
  }
  return out
})

// v3.19.44: __bt_peers__ 是后端塞进 nodes 的分组占位 (addr 为空), 不是真节点 —
// 此前混进节点一览, 空 IP 喂 FlagBadge 渲染出 '??' 徽章 + ":端口" 假地址
// (用户看到的"两个问号/地理位置未知")。真实对端在下方 peers 区按 IP 展示。
const nodes = computed<BTNodeInfo[]>(() => (bt.value?.nodes ?? []).filter((n) => n.status !== 'bt-peers'))
const seedCount = computed(() => bt.value?.seeds?.length ?? 0)
</script>

<template>
  <div class="card">
    <div class="flex items-center gap-3 mb-3">
      <h3 class="text-xs font-bold uppercase tracking-wider text-slate-400">{{ t('cluster.bt.title') }}</h3>
      <span v-if="bt" class="count">{{ t('cluster.bt.peerSummary', { peers: allPeers.length, seeds: seedCount }) }}</span>
      <span v-if="bt?.dht?.enabled" class="text-[10px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{{ t('cluster.bt.dhtOn') }}</span>
    </div>

    <!-- v3.18.4 节点一览: 全部集群节点 + IP (tracker 内置名单) -->
    <div v-if="nodes.length" class="mb-3">
      <div class="text-[10.5px] text-slate-400 font-semibold uppercase tracking-wider mb-1.5">{{ t('cluster.bt.nodeOverview') }}</div>
      <div v-for="n in nodes" :key="n.id" class="peer-row">
        <div class="min-w-0 flex items-center">
          <FlagBadge :ip="n.addr" />
          <span class="text-xs font-semibold text-slate-700 truncate">{{ n.name || n.id.slice(0, 12) }}</span>
          <span v-if="n.is_local" class="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600">{{ t('cluster.bt.localNode') }}</span>
        </div>
        <span class="font-mono text-xs text-slate-500 truncate">{{ n.addr }}:{{ n.bt_port }}</span>
        <span class="flex items-center gap-1.5">
          <span class="text-[10px] px-1.5 py-0.5 rounded-full"
                :class="n.status === 'online' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'">
            {{ n.status === 'online' ? t('cluster.bt.online') : t('cluster.bt.offline') }}
          </span>
          <span v-if="!n.is_local" class="text-[10px] px-1.5 py-0.5 rounded-full"
                :class="n.announced ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'">
            {{ n.announced ? t('cluster.bt.announced') : t('cluster.bt.notAnnounced') }}
          </span>
        </span>
      </div>
    </div>

    <!-- Tracker peers -->
    <div v-if="allPeers.length" class="mb-3">
      <div class="text-[10.5px] text-slate-400 font-semibold uppercase tracking-wider mb-1.5">{{ t('cluster.bt.peersTitle') }}</div>
      <div v-for="(p, i) in allPeers" :key="i" class="peer-row">
        <div class="min-w-0 flex items-center">
          <template v-if="p.loopback">
            <span class="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600">{{ t('cluster.bt.localNode') }}</span>
            <span class="text-xs font-semibold text-slate-500">{{ t('cluster.bt.selfAnnounce') }}</span>
          </template>
          <template v-else>
            <FlagBadge :ip="p.ip" />
            <span class="font-mono text-xs font-semibold text-slate-700 truncate">{{ p.ip }}:{{ p.port }}</span>
          </template>
        </div>
        <span class="text-[10.5px] px-2 py-0.5 rounded-full"
              :class="p.seeder ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'">
          {{ p.seeder ? t('cluster.bt.seeding') : t('cluster.bt.downloading') }}
        </span>
      </div>
    </div>
    <div v-else class="text-xs text-slate-400 py-1">{{ t('cluster.bt.noPeers') }}</div>

    <!-- 做种列表 -->
    <div v-if="bt?.seeds?.length" class="mt-3">
      <div class="text-[10.5px] text-slate-400 font-semibold uppercase tracking-wider mb-1.5">{{ t('cluster.bt.seedsTitle') }}</div>
      <div v-for="s in bt.seeds" :key="s.info_hash" class="peer-row">
        <div class="min-w-0">
          <div class="text-xs font-semibold text-slate-700 truncate">{{ s.name }}</div>
          <div class="text-[10px] text-slate-400">{{ s.peers }} peer · {{ s.uploaded > 0 ? formatBytes(s.uploaded) + ' ' + t('cluster.bt.uploaded') : t('cluster.bt.noUpload') }}</div>
        </div>
        <span class="text-[10.5px] text-slate-500 text-right">{{ t('cluster.bt.ratio') }} {{ s.ratio.toFixed(2) }}</span>
        <span class="text-[10px] px-1.5 py-0.5 rounded-full text-center"
              :class="s.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'">
          {{ s.active ? t('cluster.bt.active') : t('cluster.bt.stopped') }}
        </span>
      </div>
    </div>

    <div v-if="!bt" class="text-xs text-slate-400 py-1">{{ t('cluster.bt.disabled') }}</div>
  </div>
</template>

<style scoped>
.card { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 14px 16px; }
.count { font-size: 10.5px; font-weight: 600; background: #eef2ff; color: #6366f1; padding: 1px 7px; border-radius: 999px; }
.peer-row { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) auto; gap: 10px; align-items: center;
  padding: 6px 10px; border-radius: 9px; margin-bottom: 4px; background: #f8fafc; border: 1px solid #e2e8f0; }
</style>
